/**
 * Scroll progress for the vertical timelines — the home page "What goes into
 * every thorough and spotless clean" steps (components/home/our_standard_v3.html)
 * and the about page "From Darwin to nationwide excellence" story
 * (components/about/our_story_v3.html).
 *
 * The markup ships every entry already carrying `is-active`, so with no JS — or
 * under prefers-reduced-motion — all of them read at full contrast. This script
 * only takes contrast *away* from the entries below the reading line, and grows
 * the orange rail down to the last one reached.
 *
 * The reading line sits at 55% of the viewport height rather than the middle:
 * an entry is "reached" once its dot has passed slightly below centre, which is
 * where the eye already is when scrolling down a page.
 *
 * A section opts in with `data-scroll-timeline`; inside it, entries carry
 * `.js-timeline-step` with a `.js-timeline-dot`, and the fillable rail carries
 * `.js-timeline-progress`.
 */
const READING_LINE_RATIO = 0.55;

class ScrollTimeline {
    constructor(section) {
        this.section = section;
        this.steps = Array.from(section.querySelectorAll('.js-timeline-step'));
        this.progress = section.querySelector('.js-timeline-progress');
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
    }

    init() {
        if (!this.steps.length) return;

        // Reduced motion: leave the markup's all-active state alone. Nothing to
        // bind, so this also spares those users the scroll handler entirely.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.update();
    }

    onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
        });
    }

    update() {
        const line = window.innerHeight * READING_LINE_RATIO;
        let lastReached = -1;

        this.steps.forEach((step, index) => {
            const dot = step.querySelector('.js-timeline-dot');
            const anchor = (dot || step).getBoundingClientRect();
            const reached = anchor.top <= line;
            step.classList.toggle('is-active', reached);
            if (reached) lastReached = index;
        });

        if (!this.progress) return;

        if (lastReached < 0) {
            this.progress.style.height = '0px';
            return;
        }

        // Rail stops at the last reached dot, measured against the track's own
        // top so it lines up whatever the step spacing is.
        const trackTop = this.progress.getBoundingClientRect().top;
        const dot = this.steps[lastReached].querySelector('.js-timeline-dot');
        const target = (dot || this.steps[lastReached]).getBoundingClientRect();
        const height = Math.max(0, target.top + target.height / 2 - trackTop);
        this.progress.style.height = `${height}px`;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document
        .querySelectorAll('[data-scroll-timeline]')
        .forEach((section) => new ScrollTimeline(section).init());
});
